import { Input } from 'antd';
import { useMemo, useState } from 'react';

import { Select } from '@/components/common/select';
import { useConfirm } from '@/components/feedback/dialog';
import { Modal } from '@/components/feedback/modal';
import { useToast } from '@/components/feedback/toast';

import { useAddParameters, useParameterTemplates } from '../hooks';
import type { OrderDetail } from '../types';

import styles from './order-detail.module.scss';

type Entry = { serialNumber: string; templateId: string };
type Props = {
  order: OrderDetail;
  /** When set, scope to one device; otherwise page through all unconfigured devices. */
  lineItemId: string | null;
  open: boolean;
  onClose: () => void;
};

export function AddParametersModal({ order, lineItemId, open, onClose }: Props) {
  const scoped = useMemo(() => {
    const unconfigured = order.lineItems.filter(
      (li) => li.parameterApplicable && !li.parameterConfigured,
    );
    return lineItemId ? unconfigured.filter((li) => li.id === lineItemId) : unconfigured;
  }, [order.lineItems, lineItemId]);

  const [index, setIndex] = useState(0);
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  const templates = useParameterTemplates(open);
  const templateOptions = useMemo(
    () => (templates.data ?? []).map((t) => ({ value: t.id, label: t.name })),
    [templates.data],
  );
  const add = useAddParameters(order.id);
  const toast = useToast();
  const { confirm } = useConfirm();

  const current = scoped[index];
  const entry = current ? (entries[current.id] ?? { serialNumber: '', templateId: '' }) : null;
  const dirty = Object.keys(entries).length > 0;
  const isLast = index >= scoped.length - 1;

  const reset = () => {
    setIndex(0);
    setEntries({});
  };

  const setField = (key: keyof Entry, value: string) => {
    if (!current) return;
    setEntries((prev) => ({
      ...prev,
      [current.id]: { ...(prev[current.id] ?? { serialNumber: '', templateId: '' }), [key]: value },
    }));
  };

  const handleClose = async () => {
    if (dirty) {
      const ok = await confirm({
        title: 'Discard changes?',
        subtitle: 'Any unsaved device and template details will be lost.',
        confirmText: 'Discard',
        cancelText: 'Continue Editing',
        danger: true,
      });
      if (!ok) return;
    }
    reset();
    onClose();
  };

  const handlePrimary = async () => {
    if (!isLast) {
      setIndex((i) => i + 1);
      return;
    }
    const built = scoped.map((d) => ({
      deviceId: d.id,
      serialNumber: entries[d.id]?.serialNumber ?? '',
      parameterTemplateId: entries[d.id]?.templateId ?? '',
    }));
    try {
      await add.mutateAsync({ entries: built });
      toast.success({ title: 'Parameters saved successfully.' });
      reset();
      onClose();
    } catch (e) {
      toast.error({ title: e instanceof Error ? e.message : 'Could not save the parameters.' });
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      onCancel={handleClose}
      title="Add Parameters"
      size="md"
      primaryText={isLast ? 'Save' : 'Next'}
      onPrimary={() => void handlePrimary()}
    >
      <p className={styles.modalHelp}>
        Enter the serial number found on the back of your device. It should be in the format:
        XXXXXX-XXXX
      </p>
      {current && entry ? (
        <>
          {scoped.length > 1 && (
            <p className={styles.deviceCounter}>
              Devices {index + 1}/{scoped.length}
            </p>
          )}
          <p className={styles.deviceName}>{current.name}</p>
          <div className={styles.modalField}>
            <span>Serial Number</span>
            <Input
              value={entry.serialNumber}
              onChange={(e) => setField('serialNumber', e.target.value)}
              placeholder="Enter serial number"
            />
          </div>
          <div className={styles.modalField}>
            <span>Parameter Template</span>
            <Select
              value={entry.templateId || undefined}
              onChange={(v) => setField('templateId', String(v))}
              options={templateOptions}
              loading={templates.isFetching}
              placeholder="Select"
            />
          </div>
        </>
      ) : (
        <p className={styles.empty}>All devices are already configured.</p>
      )}
    </Modal>
  );
}
